import type {
  AreaRow,
  HargaKomoditasData,
  KomoditasItem,
  KomoditasMeta,
  ProvinsiHarga,
} from '@/types/harga-komoditas'

const BASE_URL = '/data/harga-komoditas'

async function fetchJson(url: string): Promise<HargaKomoditasData> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<HargaKomoditasData>
}

export async function fetchHargaLatest(): Promise<HargaKomoditasData> {
  return fetchJson(`${BASE_URL}/latest.json`)
}

export async function fetchHargaByTanggal(tanggal: string): Promise<HargaKomoditasData> {
  return fetchJson(`${BASE_URL}/${tanggal}.json`)
}

/**
 * 4 komoditas unggas yang ditampilkan di sisi publik — urutan di sini
 * menentukan urutan kolom tabel dan kartu ringkasan.
 * Sumber: variant_id Kemendag (tipe_komoditas=1).
 */
export const KOMODITAS_UNGGAS: KomoditasMeta[] = [
  { variant_id: 27, nama: 'Daging Ayam Ras', satuan: 'kg' },
  { variant_id: 43, nama: 'Daging Ayam Kampung', satuan: 'Ekor' },
  { variant_id: 25, nama: 'Telur Ayam Ras', satuan: 'kg' },
  { variant_id: 44, nama: 'Telur Ayam Kampung', satuan: 'kg' },
]

/** Label pendek untuk header kolom & kartu di layar sempit. */
export const KOMODITAS_LABEL: Record<number, { pendek: string; ikon: string }> = {
  27: { pendek: 'Ayam Ras', ikon: '🍗' },
  43: { pendek: 'Ayam Kampung', ikon: '🐓' },
  25: { pendek: 'Telur Ras', ikon: '🥚' },
  44: { pendek: 'Telur Kampung', ikon: '🥚' },
}

/** Komoditas yang benar-benar ada di dokumen; fallback ke daftar bawaan. */
export function komoditasOf(data: HargaKomoditasData | null): KomoditasMeta[] {
  if (data?.komoditas?.length) return data.komoditas
  return KOMODITAS_UNGGAS
}

/**
 * Nama wilayah versi ringkas untuk tabel & kartu.
 *
 * Nama resmi terlalu panjang untuk kolom sempit ("Kabupaten Kulon Progo",
 * "Kota Administrasi Jakarta Selatan") sehingga terpotong. Aturannya:
 * "Kabupaten X" → "Kab. X", "Kota X" → "X" (kota sudah jelas dari namanya).
 */
export function ringkasNamaWilayah(nama: string): string {
  const n = nama.trim().replace(/\s+/g, ' ')
  if (n.startsWith('Kabupaten Administrasi ')) return `Kab. ${n.slice('Kabupaten Administrasi '.length)}`
  if (n.startsWith('Kota Administrasi ')) return n.slice('Kota Administrasi '.length)
  if (n.startsWith('Kabupaten ')) return `Kab. ${n.slice('Kabupaten '.length)}`
  if (n.startsWith('Kota ')) return n.slice('Kota '.length)
  return n
}

function indexKomoditas(list: KomoditasItem[]): Record<number, KomoditasItem | undefined> {
  const out: Record<number, KomoditasItem | undefined> = {}
  for (const k of list) out[k.variant_id] = k
  return out
}

/**
 * Baris tabel untuk satu provinsi: baris rata-rata provinsi lebih dulu,
 * lalu kab/kota-nya. Dokumen v1 (tanpa `wilayah`) menghasilkan 1 baris saja.
 */
export function areaRowsOf(prov: ProvinsiHarga | undefined): AreaRow[] {
  if (!prov) return []
  const rows: AreaRow[] = [
    {
      kode: prov.kode,
      // Provinsinya sudah terpilih di atas tabel, jadi mengulang namanya di
      // sini hanya membuat baris ini terpotong.
      nama: 'Rata-rata provinsi',
      tipe: 'provinsi',
      harga: indexKomoditas(prov.komoditas ?? []),
    },
  ]
  for (const w of prov.wilayah ?? []) {
    rows.push({
      kode: w.kode,
      nama: ringkasNamaWilayah(w.nama),
      tipe: 'wilayah',
      ibukota_provinsi: w.ibukota_provinsi,
      harga: indexKomoditas(w.komoditas ?? []),
    })
  }
  return rows
}

/** Ringkasan nasional: rata-rata sederhana antar provinsi yang punya harga. */
export function rataNasional(
  data: HargaKomoditasData | null,
  variantId: number,
): { harga: number; delta: number; persen: number } | null {
  if (!data) return null
  let total = 0
  let n = 0
  // Pembanding dihitung dengan pembaginya SENDIRI: tidak semua provinsi punya
  // harga pembanding, dan membaginya dengan n akan menekan rata-rata pembanding
  // sehingga persen perubahan tampak melonjak.
  let totalBanding = 0
  let nBanding = 0
  for (const p of data.provinsi) {
    const k = p.komoditas?.find((x) => x.variant_id === variantId)
    if (!k || k.harga <= 0) continue
    total += k.harga
    n++
    if (k.harga_pembanding > 0) {
      totalBanding += k.harga_pembanding
      nBanding++
    }
  }
  if (n === 0) return null
  const harga = Math.round(total / n)
  const banding = nBanding > 0 ? Math.round(totalBanding / nBanding) : 0
  const delta = banding > 0 ? harga - banding : 0
  return { harga, delta, persen: banding > 0 ? (delta / banding) * 100 : 0 }
}

/** Jumlah provinsi yang benar-benar melaporkan komoditas ini. */
export function jumlahProvinsiLapor(
  data: HargaKomoditasData | null,
  variantId: number,
): number {
  if (!data) return 0
  return data.provinsi.filter((p) => {
    const k = p.komoditas?.find((x) => x.variant_id === variantId)
    return !!k && k.harga > 0
  }).length
}

/** Wilayah termurah & termahal untuk satu komoditas — bahan sorotan di beranda. */
export function ekstremHarga(
  data: HargaKomoditasData | null,
  variantId: number,
): { termurah: { nama: string; harga: number }; termahal: { nama: string; harga: number } } | null {
  if (!data) return null
  let min: { nama: string; harga: number } | null = null
  let max: { nama: string; harga: number } | null = null
  for (const p of data.provinsi) {
    const k = p.komoditas?.find((x) => x.variant_id === variantId)
    if (!k || k.harga <= 0) continue
    if (!min || k.harga < min.harga) min = { nama: p.nama, harga: k.harga }
    if (!max || k.harga > max.harga) max = { nama: p.nama, harga: k.harga }
  }
  return min && max ? { termurah: min, termahal: max } : null
}

export function formatRupiah(amount: number): string {
  if (!amount || amount <= 0) return '-'
  return `Rp ${amount.toLocaleString('id-ID')}`
}

/** Rupiah ringkas untuk kartu sempit: Rp 35rb / Rp 1,2jt. */
export function formatRupiahRingkas(amount: number): string {
  if (!amount || amount <= 0) return '-'
  if (amount >= 1_000_000) return `Rp ${(amount / 1_000_000).toFixed(1).replace('.', ',')}jt`
  if (amount >= 1_000) return `Rp ${Math.round(amount / 1_000)}rb`
  return `Rp ${amount}`
}

export function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

export function getPreviousWeekday(date: Date): Date {
  const d = new Date(date)
  do {
    d.setDate(d.getDate() - 1)
  } while (isWeekend(d))
  return d
}

export function getLatestWeekday(): Date {
  const today = new Date()
  return isWeekend(today) ? getPreviousWeekday(today) : today
}

/** YYYY-MM-DD di zona waktu lokal (bukan UTC — toISOString bisa geser sehari). */
export function formatDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function formatTanggalPanjang(tanggal: string): string {
  const date = new Date(tanggal + 'T00:00:00')
  return date.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function formatUpdatedAt(updatedAt: string): string {
  return new Date(updatedAt).toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
